import { FC } from 'react';

import { Tooltip } from 'antd';

import { colors } from '@theme';
import { Schemas } from '@api-client/generated/types';

import { getDocumentErrorReason } from '@utils';

import * as S from './styled';

type Status = Schemas.Document['adminStatus'];
type BobStatus = Schemas.Document['bobStatus'];

type StatusTagProps = {
  status: Status;
  bobStatus?: BobStatus;
  /**
   * The error facet. Passed as the raw fields rather than a ready-made flag so the tag and the details
   * card ask the same helper the same question — an error is an overlay on the status, and the two
   * screens disagreeing about it is the bug this replaces [ENG-1256].
   */
  hasError?: boolean;
  errorCode?: string | null;
  bobPendingAt?: string | null;
  /**
   * The status the facet is judged against, when it differs from the one being displayed. The details
   * page shows an optimistic label while a status change is in flight; the error is a property of the
   * persisted row, and reading it off the optimistic label would make the marker disappear mid-edit.
   */
  facetStatus?: Status;
}

type ParamsByStatus = {
  label: string;
  bgColor: string;
  textColor: string;
}

const getParamsByStatus = (status: Status, bobStatus?: BobStatus): ParamsByStatus => {
  // No `failed` special case here any more. A document whose sync failed is still a New document —
  // that is what its status column says and what the retry acts on — so it keeps its label and gains
  // the marker below instead of being renamed.
  switch (status) {
    case 'ready':
      return {
        label: 'New',
        bgColor: colors.strokeDarkGrey,
        textColor: colors.textBlue400,
      };

    case 'processing':
      return {
        label: bobStatus === 'pending' ? 'In work (automatic)' : 'In work (manual)',
        bgColor: colors.bgBrightBlue,
        textColor: colors.link,
      };

    case 'requested_changes':
      return {
        label: 'Error',
        bgColor: colors.iconWarning,
        textColor: colors.error,
      };

    case 'processed':
      return {
        label: bobStatus === 'succeeded' ? 'Booked (automatic)' : 'Booked (manual)',
        bgColor: colors.greenLight,
        textColor: colors.success,
      };
    default:
      return {
        label: 'New',
        bgColor: colors.strokeDarkGrey,
        textColor: colors.textBlue400,
      };
  }
};

const DocumentsStatusTag: FC<StatusTagProps> = ({
  status,
  bobStatus,
  hasError,
  errorCode,
  bobPendingAt,
  facetStatus,
}) => {
  if (!status) {
    return null;
  }

  const { label, bgColor, textColor } = getParamsByStatus(status, bobStatus);
  const errorReason = getDocumentErrorReason({
    adminStatus: facetStatus ?? status,
    bobStatus,
    hasError,
    errorCode,
    bobPendingAt,
  });

  return (
    <S.Wrapper>
      <S.Status bgColor={bgColor} textColor={textColor}>
        {label}
      </S.Status>

      {errorReason && (
        // Focusable and focus-triggered: the reason is the only place the tooltip carries it, and a
        // hover-only marker on a non-focusable span means a keyboard user cannot read it at all.
        <Tooltip title={errorReason} trigger={['hover', 'focus']}>
          <S.ErrorMarker aria-label={`Error: ${errorReason}`} role="img" tabIndex={0}>
            ⚠
          </S.ErrorMarker>
        </Tooltip>
      )}
    </S.Wrapper>
  );
};

export default DocumentsStatusTag;
